import React from 'react';
import { Link } from 'react-router-dom';
import SharedLayout from '@/components/SharedLayout';
import SubpageNavigation from '@/components/SubpageNavigation';
import PomodoroGallery from '@/components/PomodoroGallery';
import { FAQ } from '@/components/FAQ';
import SEOHead from '@/components/SEOHead';
import { CANONICAL_URLS } from '@/utils/canonicalUrls';
import { faqData, generateFAQSchema } from '@/data/faqData';

const Overview = () => {

  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Free Pomodoro Timer Online",
    "alternateName": "Aesthetic Pomodoro Timer",
    "description": "Free online Pomodoro timer with aesthetic backgrounds, customizable focus sessions, task management, Kanban boards, and Eisenhower Matrix. No signup required.",
    "url": "https://freepomodorotimer.com",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web Browser",
    "browserRequirements": "Requires JavaScript enabled",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1250",
      "bestRating": "5"
    },
    "featureList": [
      "25-minute Pomodoro timer sessions",
      "Customizable work and break intervals",
      "Audio notifications and alerts",
      "Task management and tracking",
      "Kanban board workflow",
      "Eisenhower priority matrix",
      "Aesthetic background images from Unsplash",
      "Dark and light mode themes",
      "No registration required",
      "Works offline"
    ]
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Use a Pomodoro Timer for Maximum Focus",
    "description": "Step-by-step guide to using the Pomodoro Technique with our free online timer for better focus and productivity.",
    "totalTime": "PT2H",
    "step": [
      { "@type": "HowToStep", "position": 1, "name": "Start the Timer", "text": "Click Start to begin your 25-minute focused work session." },
      { "@type": "HowToStep", "position": 2, "name": "Focus on One Task", "text": "Work on a single task with complete concentration until the timer rings." },
      { "@type": "HowToStep", "position": 3, "name": "Take a Short Break", "text": "Take a 5-minute break to walk around, breathe, and relax." },
      { "@type": "HowToStep", "position": 4, "name": "Repeat the Cycle", "text": "After 4 Pomodoros, take a longer break of 15–30 minutes." },
      { "@type": "HowToStep", "position": 5, "name": "Track Your Progress", "text": "Continue as needed to stay productive without burning out." }
    ]
  };

  const faqSchema = generateFAQSchema(faqData);
  
  const combinedSchema = [webAppSchema, howToSchema, faqSchema];

  return (
    <>
      <SEOHead
        title="Free Online Pomodoro Timer | Aesthetic Focus Timer 2026"
        description="Free aesthetic Pomodoro timer with beautiful backgrounds, customizable intervals, Kanban boards & Eisenhower Matrix. Boost focus for studying, work & ADHD — no signup required."
        keywords="pomodoro timer, free pomodoro timer, aesthetic pomodoro timer, focus timer, productivity timer, study timer, pomodoro technique, ADHD timer, online timer, work timer, concentration timer, time management tool, pomodoro timer online free"
        canonicalUrl={CANONICAL_URLS.home}
        schemaData={combinedSchema}
      />
      <SharedLayout>
        <article className="text-left px-6 py-12 max-w-4xl mx-auto">
          <SubpageNavigation />
              
              <header>
                <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800 dark:text-gray-100">Free Online Pomodoro Timer — Aesthetic Focus & Productivity Tool</h1>
                <p className="text-xl mb-6 text-gray-700 dark:text-gray-200">
                  This free Pomodoro timer is a beautiful, distraction-free online tool designed to help you stay focused, 
                  manage your time, and get more done using the proven <strong>Pomodoro Technique</strong>. Featuring aesthetic backgrounds from Unsplash, 
                  customizable intervals, and built-in task management.
                </p>
              </header>
              
              <section className="mb-12">
                <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">What Is the Pomodoro Technique?</h2>
                <p className="mb-8 text-gray-700 dark:text-gray-200">
                  The <strong>Pomodoro Technique</strong> is a time management method developed by Francesco Cirillo in the late 1980s. It breaks your work into short, focused intervals—usually <strong>25 minutes</strong>—called "Pomodoros," 
                  followed by a <strong>5-minute break</strong>. After every four Pomodoros, you take a longer break of 15 to 30 minutes. 
                  This rhythm helps maintain concentration, reduce fatigue, and make consistent progress on tasks.
                </p>
                <p className="mb-8 text-gray-700 dark:text-gray-200">
                  Whether you're <strong>studying for exams</strong>, coding, writing, or <strong>working remotely</strong>, this aesthetic Pomodoro timer helps you build momentum, 
                  stay accountable, and beat procrastination—all without needing to sign up or download anything.
                </p>
              </section>
              
              <section className="mb-12">
                <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">Complete Free Productivity Toolkit</h2>
                <p className="mb-8 text-gray-700 dark:text-gray-200">
                  Our free productivity suite combines proven time management techniques to help you work smarter, not harder. 
                  Choose the right tool for your current needs or combine them for maximum effectiveness.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {/* Pomodoro Timer Card */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl font-bold mb-3 text-gray-800 dark:text-gray-100">🍅 Pomodoro Timer</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                      Free 25-minute focus sessions with customizable breaks. Perfect for <strong>ADHD</strong>, studying, deep work, and beating procrastination.
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="text-xs text-gray-500 dark:text-gray-400">✓ Customizable intervals</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">✓ Audio notifications</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">✓ Aesthetic backgrounds</div>
                    </div>
                    <Link 
                      to="/pomodoro" 
                      className="inline-block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      Learn Pomodoro Technique →
                    </Link>
                  </div>

                  {/* Eisenhower Matrix Card */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl font-bold mb-3 text-gray-800 dark:text-gray-100">📊 Eisenhower Matrix</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                      Prioritize tasks by urgency and importance. Master the <strong>4-quadrant system</strong> used by presidents, CEOs, and top performers.
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="text-xs text-gray-500 dark:text-gray-400">✓ Four priority quadrants</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">✓ Decision making framework</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">✓ Stress reduction</div>
                    </div>
                    <Link 
                      to="/eisenhower" 
                      className="inline-block w-full text-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      Master Task Prioritization →
                    </Link>
                  </div>

                  {/* Kanban Board Card */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl font-bold mb-3 text-gray-800 dark:text-gray-100">📋 Kanban Board</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                      Visualize your workflow and limit work-in-progress. The <strong>Toyota-inspired method</strong> for continuous improvement and team productivity.
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="text-xs text-gray-500 dark:text-gray-400">✓ Visual workflow management</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">✓ Work-in-progress limits</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">✓ Continuous improvement</div>
                    </div>
                    <Link 
                      to="/kanban" 
                      className="inline-block w-full text-center bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      Learn Kanban Method →
                    </Link>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6 mb-8">
                  <h3 className="text-lg font-bold mb-3 text-blue-800 dark:text-blue-200">💡 Pro Tip: Combine Techniques for Maximum Productivity</h3>
                  <p className="text-blue-700 dark:text-blue-300 text-sm mb-3">
                    Use the <Link to="/eisenhower" className="font-semibold underline">Eisenhower Matrix</Link> to prioritize your tasks, organize them in a <Link to="/kanban" className="font-semibold underline">Kanban board</Link>, 
                    then execute with <Link to="/pomodoro" className="font-semibold underline">Pomodoro sessions</Link> for maximum productivity.
                  </p>
                  <div className="text-xs text-blue-600 dark:text-blue-400">
                    Matrix → Kanban → Pomodoro = Ultimate productivity system
                  </div>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">How to Use This Free Pomodoro Timer</h2>
                <ol className="list-decimal pl-6 mb-8 text-gray-700 dark:text-gray-200">
                  <li className="mb-2"><strong>Click Start</strong> to begin your 25-minute work session.</li>
                  <li className="mb-2"><strong>Focus completely</strong> on one task until the timer rings.</li>
                  <li className="mb-2"><strong>Take a 5-minute break</strong>—walk around, breathe, relax.</li>
                  <li className="mb-2"><strong>Repeat the cycle.</strong> After 4 Pomodoros, take a longer break of 15–30 minutes.</li>
                  <li className="mb-2"><strong>Continue as needed</strong> to stay productive without burning out.</li>
                </ol>
                <p className="mb-8 text-gray-700 dark:text-gray-200">
                  This timer works instantly in any modern browser—no setup, no downloads, no distractions. Works on <strong>desktop, mobile, and tablet</strong>.
                </p>
              </section>

              <section className="mb-12">
                <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Why Use a Pomodoro Timer for Studying & Work?</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-200">
                  Research shows that time-boxed work intervals significantly improve <strong>focus, retention, and productivity</strong>. The Pomodoro Technique is especially effective for:
                </p>
                <ul className="list-disc pl-6 mb-8 text-gray-700 dark:text-gray-200">
                  <li className="mb-2"><strong>Students</strong> — structure study sessions, improve retention, and avoid cramming fatigue</li>
                  <li className="mb-2"><strong>Remote workers</strong> — maintain focus without office structure and beat work-from-home distractions</li>
                  <li className="mb-2"><strong>People with ADHD</strong> — short intervals make tasks feel manageable and reduce overwhelm</li>
                  <li className="mb-2"><strong>Writers & creatives</strong> — overcome creative blocks with structured sprint sessions</li>
                  <li className="mb-2"><strong>Developers & programmers</strong> — ideal for debugging, coding sprints, and code reviews</li>
                </ul>
              </section>
              
              <section className="mb-12">
                <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">Aesthetic Backgrounds for Calm & Clarity</h2>
                <div className="mb-8">
                  <p className="mb-4 text-gray-700 dark:text-gray-200">We've partnered with Unsplash to bring you some of the most beautiful and calming photography from around the world, carefully selected to help you escape the noise of everyday life, focus on what matters, and get things done. From sunlit beaches and peaceful forests to mountain peaks and golden sunsets, each aesthetic scene transforms your workspace into a window to somewhere inspiring.</p>
                </div>
                
                <PomodoroGallery />
                
                <div className="mt-8">
                  <p className="text-gray-700 dark:text-gray-200">
                    A clear, beautiful environment leads to a clear, productive mind. Let these inspiring visuals enhance your 
                    Pomodoro sessions and help you achieve <strong>deep focus</strong> throughout your workday.
                  </p>
                </div>
              </section>
              
          <FAQ />
        </article>
      </SharedLayout>

      <style>
        {`
          .glass-panel {
            border-radius: 16px !important;
            backdrop-filter: blur(10px);
            background: rgba(0, 0, 0, 0.3);
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
            transition: all 0.3s ease;
          }
          .nav-bar img {
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
            filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.7));
          }
          .seo-content {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border-width: 0;
          }
        `}
      </style>
    </>
  );
};

export default Overview;